import { getCookie, setCookie } from 'util/cookie-utils'
import { hostsConfig, turnToConfig } from 'init/config'
import log from 'util/logger'

export type EventType = 'load'
export type Widget = 'chatter' | 'feed-comment-capture' | 'comments-pinboard' |
              'comments-pinboard-teaser' | 'gallery' | 'qa' |
              'reviews-list' | 'reviews-summary' | 'spectrum' |
              'subdimension-teaser' | 'top-comments' | 'vc-pinboard' | 'teaser'

interface TrackingEvent {
  cts: string
  svid: string
  et: EventType
  sk: string
  sku?: string
  wg: Widget
  ua: string
}

const eventQueue = []
let isSendingEvent = false

const TTSVID_COOKIE_NAME = 'TTSVID'
const VISIT_SESSION_TIMEOUT_MINUTES = 30

function queueEvent(widget: Widget, eventType: EventType, sku: string = null) {
  if (turnToConfig.disableTargetingCookies) {
    log.info('targeting cookies disabled, skipping event')
    return
  }
  const event = { wg: widget, et: eventType, sku }
  // TT-14254 - Temporarily stop calls with the exception of reviews-summary
  if (widget !== 'reviews-summary') {
    return
  }
  eventQueue.push(event)
  postEvents()
}

function postEvents() {
  if (!isSendingEvent) {
    isSendingEvent = true
    while (eventQueue.length) {
      const el = eventQueue.shift()
      const event = createEvent(el.wg, el.et, el.sku)
      if (!event.ua || event.ua.indexOf('bot') > -1) {
        continue
      }
      postWebEvent(event)
    }
    isSendingEvent = false
  }
}

function createEvent(widget: Widget, eventType: EventType, sku: string) {
  const event  = {
    cts: new Date().toJSON(),
    svid: getSVID(),
    et: eventType,
    sk: hostsConfig.siteKey,
    wg: widget,
    ua: navigator.userAgent
  } as TrackingEvent
  if (sku) {
    event.sku = sku
  }

  return event
}

function postWebEvent(payload: TrackingEvent) {
  const body = JSON.stringify(payload)
  const xhr: TTXMLHttpRequest<any> = new XMLHttpRequest()
  const url = hostsConfig.analyticsHost
  xhr.open('POST', url)
  xhr.setRequestHeader('Content-Type', 'application/json')
  xhr.send(body)
}

function getSVID() {
  let svid = getCookie(TTSVID_COOKIE_NAME)
  if (!svid) {
    svid = uuidv4()
  }
  setCookie(TTSVID_COOKIE_NAME, svid, VISIT_SESSION_TIMEOUT_MINUTES * 60)

  return svid
}

// General approach taken from:
// https://medium.com/teads-engineering/generating-uuids-at-scale-on-the-web-2877f529d2a2
function uuidv4() {
  try {
    const url = URL.createObjectURL(new Blob())
    // should create a url like: blob:http://localhost:9999/11636509-9884-4eb5-bc21-b8403f41d7f2
    let uuidIndex = url ? url.lastIndexOf('/') + 1 : -1
    // check for IE style (e.g. just blob:86AB2D82-BBE6-434B-ABFF-EF27986E8BFC)
    if (uuidIndex === 0 && url && url.indexOf('blob:') === 0) {
      uuidIndex = 5
    }
    if (uuidIndex > -1) {
      return url.substring(uuidIndex)
    }
    return uuidFallback()
  } catch (e) {
    return uuidFallback()
  }
}

function uuidFallback() {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    // based on solution at https://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid
    // modified to play nice with typescript
    // for the most part, converting byte hex values to 2 digit numbers (some times 0 left-padded)
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) => {
      const n = parseInt(c, 10)
      return (n ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> n / 4).toString(16)
    })
  } else {
    // older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
    })
  }
}

export default { queueEvent }
